import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../services/common.service';
import { CaseService } from '../../services/case.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-contact-admin-modal',
  template: `
    <ion-header>
      <ion-toolbar color="primary">
        <ion-title>{{'ACCESS_DENIED.CONTACT_ADMINISTRATOR' | translate}}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="dismiss()">
            <ion-icon name="close-outline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="modal-content">
        <div class="message-box">
          <p class="main-message">{{'ACCESS_DENIED.CONTACT_ADMINISTRATOR_MESSAGE' | translate}}</p>
          <p class="sub-message">{{'ACCESS_DENIED.CONTACT_ADMINISTRATOR_IP_MESSAGE' | translate: { current_ip: currentIp } }}</p>
        </div>

        <form [formGroup]="requestAccess" (ngSubmit)="submitRequest()">
          <ion-item>
            <ion-label position="stacked">{{'ACCESS_DENIED.YOUR_NAME' | translate}} <ion-text color="danger">*</ion-text></ion-label>
            <ion-input formControlName="name" placeholder="{{'ACCESS_DENIED.ENTER_YOUR_FULL_NAME' | translate}}"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">{{'ACCESS_DENIED.EMAIL_ADDRESS' | translate}} <ion-text color="danger">*</ion-text></ion-label>
            <ion-input type="email" formControlName="email" placeholder="{{'ACCESS_DENIED.ENTER_EMAIL_ADDRESS' | translate}}"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">{{'ACCESS_DENIED.REASON_FOR_ACCESS' | translate}} <ion-text color="danger">*</ion-text></ion-label>
            <ion-textarea
              formControlName="reason"
              rows="4"
              placeholder="{{'ACCESS_DENIED.ENTER_REASON_FOR_ACCESS' | translate}}"
            ></ion-textarea>
          </ion-item>

          <div class="ion-padding">
            <ion-button expand="block" type="submit" [disabled]="requestAccess.invalid || isSubmitting">
              <ion-spinner *ngIf="isSubmitting" name="crescent"></ion-spinner>
              <span *ngIf="!isSubmitting">{{'ACCESS_DENIED.SUBMIT_REQUEST' | translate}}</span>
            </ion-button>
          </div>
        </form>
      </div>
    </ion-content>
  `,
  styles: [`
    :host {
      --width: 90%;
      --max-width: 500px;
      --height: auto;
      --max-height: 90vh;
      --border-radius: 16px;
      --box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    }

    ion-content {
      --background: var(--ion-color-light);
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      padding: 1rem;
      margin: 0.5rem;
      box-shadow: var(--box-shadow);
    }

    .message-box {
      background: var(--ion-color-light);
      border-radius: 12px;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .main-message {
      color: var(--ion-color-dark);
      font-size: 1.1em;
      font-weight: 500;
      margin: 0 0 0.5rem 0;
      line-height: 1.5;
    }

    .sub-message {
      color: var(--ion-color-medium);
      font-size: 0.9em;
      margin: 0;
      line-height: 1.4;
    }

    ion-item {
      --padding-start: 0;
      margin-bottom: 1rem;
      --background: transparent;
    }

    ion-textarea {
      border: 1px solid var(--ion-color-light-shade);
      border-radius: 8px;
      --padding-start: 8px;
      --padding-end: 8px;
      --padding-top: 8px;
      --padding-bottom: 8px;
      margin-top: 0.5rem;
    }

    ion-spinner {
      margin-right: 8px;
    }

    @media (max-width: 576px) {
      :host {
        --width: 95%;
      }

      .modal-content {
        padding: 0.75rem;
      }

      .message-box {
        padding: 0.75rem;
      }

      .main-message {
        font-size: 1em;
      }
    }
  `]
})
export class ContactAdminModalComponent implements OnInit {
  requestAccess: FormGroup;
  isSubmitting = false;
  currentIp: string;

  constructor(
    private modalCtrl: ModalController,
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private caseService: CaseService,
    private translate: TranslateService
  ) {
    this.currentIp = localStorage.getItem('last_checked_ip') || 'Unknown';
  }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.requestAccess = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      reason: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  async dismiss() {
    await this.modalCtrl.dismiss();
  }

  async submitRequest() {
    if (this.requestAccess.valid) {
      this.isSubmitting = true;

      const formData = {
        ...this.requestAccess.value,
        ip_address: this.currentIp,
        type: 'access_request'
      };

      this.caseService.requestAccess(formData).subscribe(
        (res: any) => {
          if (res.result) {
            this.commonService.showToast(this.translate.instant('ACCESS_DENIED.REQUEST_SUBMITTED_SUCCESSFULLY'), 'success');
            this.dismiss();
          } else {
            this.commonService.showToast(res.message || this.translate.instant('ACCESS_DENIED.REQUEST_FAILED'), 'danger');
          }
          this.isSubmitting = false;
        },
        (error) => {
          console.log(error);
          this.commonService.showToast(this.translate.instant('ACCESS_DENIED.REQUEST_FAILED_MESSAGE'), 'danger');
          this.isSubmitting = false;
        }
      );
    }
  }
}